
import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, ElementRef, AfterViewInit,
  ChangeDetectorRef, Inject, Renderer2, Output, EventEmitter, Input, OnInit, OnDestroy
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { PassengerArrangement, PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { SearchType } from '@search-bar/entities/search-type';
import { ChangeInputPayload } from '@shared/base-search-bar/entities/search-input-payload';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { FlightType } from '@search-bar/entities/flight-type';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { SearchBarMobileService } from '@search-bar/services/search-bar-mobile.service';
import { AmplitudeEventParams } from '@amplitude/interfaces/amplitude-events';
import { SearchBarAbstractComponent } from '@search-bar/abstracts/search-bar-abstract.component';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';

@Component({
  selector: 'app-search-bar-buses-mobile',
  templateUrl: './search-bar-buses-mobile.component.html',
  styleUrls: ['./search-bar-buses-mobile.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class SearchBarBusesMobileComponent extends SearchBarAbstractComponent implements AfterViewInit, OnInit, OnDestroy {

  @Input() searchType: SearchType;
  @Input() passengerArrangement: PassengerArrangement[];
  @Input() autoCompletePlaces: AutocompletePlaces;
  @Input() departurePlace: AutocompletePlace;
  @Input() departureDate: Date;
  @Input() arrivalPlace: AutocompletePlace;
  @Input() arrivalDate: Date;
  @Input() defaultPlace: SelectAutocompletePlace;
  @Input() isLoadingAutocompletePlaces: boolean;
  @Input() isResultsPage: boolean;

  @Output() emitArrangement: EventEmitter<PassengerArrangement> = new EventEmitter<PassengerArrangement>();
  @Output() selectCalendar: EventEmitter<Date[]> = new EventEmitter<Date[]>();
  @Output() placeInputChanged: EventEmitter<ChangeInputPayload> = new EventEmitter<ChangeInputPayload>();
  @Output() selectPlace: EventEmitter<SelectAutocompletePlace> = new EventEmitter<SelectAutocompletePlace>();
  @Output() selectBusType: EventEmitter<FlightType> = new EventEmitter<FlightType>();
  @Output() swapLocationsEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() showMobileCalendarEvent: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();
  @Output() removeAutoCompletePlaces: EventEmitter<void> = new EventEmitter<void>();
  @Output() amplitudeEvent: EventEmitter<AmplitudeEventParams> = new EventEmitter<AmplitudeEventParams>();
  
  @ViewChild('options') public options: ElementRef;

  public eventPath: BehaviorSubject<EventTarget[]> = new BehaviorSubject<EventTarget[]>([]);
  public showBusOptionSelection: boolean = false;
  public activeOption: string;
  public searching: boolean = false;
  public showCalendarInput: boolean = false;
  public activeCalendarType: CalendarField = CalendarField.departure;
  public departure: PlaceType = PlaceType.DEPARTURE;
  public arrival: PlaceType = PlaceType.ARRIVAL;
  public isSubmitted: boolean = false;

  private moveY: number = 0;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private renderer: Renderer2,
    private searchBarMobileService: SearchBarMobileService,
    private domManipuleService: DomManipulateService,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super()
  }

  ngOnInit(): void {
    this.domManipuleService.setBodyScroll(false);
  }

  ngAfterViewInit(): void {
    if (this.isBrowser && this.options) {
      this.renderer.listen(this.options.nativeElement, 'touchstart', (event) => this.onTouchStart(event));
      this.renderer.listen(this.options.nativeElement, 'touchend', (event) => this.onTouchEnd(event));
    }
  }

  ngOnDestroy(): void {
    this.domManipuleService.setBodyScroll(true);
  }

  get passengerArrangementOnlySeats(): PassengerArrangement[] {
    return this.passengerArrangement.filter(arrangement => arrangement.id === PassengerType.ADULTS);
  }

  get arrowIcon(): Icon {
    return this.searchBarMobileService.arrowIcon(this.isOneWayTrip, true);
  }

  get isEmptyDepartureDate(): boolean {
    return this.searchBarMobileService.isEmptyDepartureDate(this.departureDate, this.isSubmitted);
  }

  get isEmptyArrivalDate(): boolean {
    return this.searchBarMobileService.isEmptyArrivalDate(this.arrivalDate, this.isSubmitted);
  }

  public isActiveBusType(type: string): boolean {
    return type === this.flightType;
  }

  public swapLocations(): void {
    this.swapLocationsEmitter.emit();
  }

  public toggleBusOption(option?: string): void {
    this.showBusOptionSelection = !this.showBusOptionSelection;
    if (option) this.activeOption = option;
  }

  public showMobileCalendar(type: CalendarField): void {
    this.activeCalendarType = type;
    this.showMobileCalendarEvent.emit(type);
  }

  public busTypeSelected(type: FlightType): void {
    this.selectBusType.emit(type);
  }

  public searchBuses(): void {
    this.isSubmitted = true;
    if (!this.disabledButton) {
      this.searching = true;
      this.emitSearch.emit();
    }
  }
  
  private onTouchStart(event: TouchEvent): void {
    if (event.touches) {
      this.moveY = event.touches[0].clientY;
    }
  }

  private onTouchEnd(event: TouchEvent): void {
    const haveMove = this.moveY !== event.changedTouches[0].clientY;
    if (event.changedTouches && haveMove) {
      const moveDirection = this.moveY < event.changedTouches[0].clientY;
      if (moveDirection) {
        this.toggleBusOption();
        this.changeDetector.detectChanges();
      }
      this.moveY = 0;
    }
  }
}
